import { EntityNoteService } from '../../api/services/EntityNoteService';
import type { Routemize_DTOs_Metadata_EntityNoteDTO } from '../../api/models/Routemize_DTOs_Metadata_EntityNoteDTO';
import type { Routemize_DTOs_Metadata_NotesBundleDTO } from '../../api/models/Routemize_DTOs_Metadata_NotesBundleDTO';
import type { Routemize_DTOs_Metadata_CreateNoteDTO } from '../../api/models/Routemize_DTOs_Metadata_CreateNoteDTO';
import { CONTACT_NOTE_DEFAULTS, INTERNAL_NOTE_DEFAULTS } from './noteKinds';

export type NoteEntityType = 'Request' | 'Appointment' | 'Contact';

/**
 * NotesManager - the one way the web app reads and writes human notes (notes consolidation, 86bbkaqna).
 * Reads come as a server-classified bundle; writes go through EntityNoteService.
 */
export class NotesManager {
  /** The appointment's notes by kind, with its request's notes by reference (Decision C). */
  async getAppointmentBundle(appointmentId: string): Promise<Routemize_DTOs_Metadata_NotesBundleDTO> {
    return EntityNoteService.getApiAppEntityNoteGetAppointmentNotesBundle(appointmentId);
  }

  /** The request's notes by kind. */
  async getRequestBundle(requestId: string): Promise<Routemize_DTOs_Metadata_NotesBundleDTO> {
    return EntityNoteService.getApiAppEntityNoteGetRequestNotesBundle(requestId);
  }

  /** Adds a staff note (Internal on a request/appointment, General on a contact). */
  async add(
    entityType: NoteEntityType,
    entityId: string,
    content: string,
    overrides?: Partial<Omit<Routemize_DTOs_Metadata_CreateNoteDTO, 'content'>>
  ): Promise<Routemize_DTOs_Metadata_EntityNoteDTO> {
    const defaults = entityType === 'Contact' ? CONTACT_NOTE_DEFAULTS : INTERNAL_NOTE_DEFAULTS;
    return EntityNoteService.postApiAppEntityNoteCreateNote(entityId, entityType, {
      ...defaults,
      ...overrides,
      content: content.trim(),
    });
  }

  /** Changes the text only, so the note keeps its Type and Title (D7). */
  async update(noteId: string, content: string): Promise<Routemize_DTOs_Metadata_EntityNoteDTO> {
    return EntityNoteService.putApiAppEntityNoteUpdateNote(noteId, { content: content.trim() });
  }

  async remove(noteId: string): Promise<void> {
    await EntityNoteService.deleteApiAppEntityNoteDeleteNote(noteId);
  }

  async togglePin(noteId: string): Promise<Routemize_DTOs_Metadata_EntityNoteDTO> {
    return EntityNoteService.postApiAppEntityNoteTogglePinNote(noteId);
  }
}

export const notesManager = new NotesManager();
